import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CampaignListItem, CreateCampaign } from '~pages/CampaignManagement/domain';

import { createCampaign, getCampaigns, getCampaignsByNextUrl } from './api';
import { CampaignFilterType, CampaignResponse } from './domain';

type Options = {
  accessFilterId?: number;
  filters?: CampaignFilterType[];
  campaignIds?: number[];
  shouldFetch?: boolean;
};

const useCampaignSearch = (search: string, options: Options = {}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [list, setList] = useState<CampaignListItem[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const { filters, accessFilterId, campaignIds, shouldFetch = true } = options;
  const stringFilters = JSON.stringify(filters);

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError(false);

      let resp: CampaignResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getCampaignsByNextUrl(nextUrl, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.campaigns]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);

    let resp: CampaignResponse | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      // resp = await getCampaigns(search, filters, accessFilterId, axiosCancelRef.current);
      resp = await getCampaigns({
        search,
        filters,
        campaignIds,
        accessFilterId,
        cancelToken: axiosCancelRef.current,
      });
    } catch (e) {
      setError(true);
      setLoading(false);
      return;
    }

    // Returns undefined if request is canceled
    if (resp === undefined) return;

    setList(resp.campaigns);
    setHasMore(resp.nextPageUrl !== null);
    setNextUrl(resp.nextPageUrl);
    setLoading(false);
  }, [search, stringFilters, campaignIds, accessFilterId]);

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  const create = useCallback(
    async (data: CreateCampaign) => {
      try {
        await createCampaign(data);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [reload],
  );

  useEffect(() => {
    setList([]);
  }, [search, stringFilters, campaignIds, accessFilterId, shouldFetch]);

  useEffect(() => {
    const load = async (
      search: string,
      filters?: CampaignFilterType[],
      campaignIds?: number[],
      accessFilterId?: number,
    ) => {
      setLoading(true);
      setError(false);

      let resp: CampaignResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        // resp = await getCampaigns(search, filters, accessFilterId, axiosCancelRef.current);
        resp = await getCampaigns({
          search,
          filters,
          campaignIds,
          accessFilterId,
          cancelToken: axiosCancelRef.current,
        });
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.campaigns]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    };

    if (shouldFetch) {
      load(search, filters, campaignIds, accessFilterId);
    }

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [search, stringFilters, campaignIds, accessFilterId, shouldFetch]);

  return { loading, error, list, hasMore, create, intersectionObserverRef };
};

export default useCampaignSearch;
